import { debounce } from 'es-toolkit';
import { useState } from 'react';
import useSWR from 'swr';

import useConfig from '@/hooks/useConfig.ts';

import { SearchResult } from '@/models/Fuse.ts';
import { GenericPatient } from '@/models/GenericPatient.ts';
import { NormalizedPatient } from '@/models/Patient.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';
import { EnrichedDataCurUser } from '@/models/data-cur/DataCurUser.ts';

import { searchUserOnAllInstances } from '@/services/patientService.ts';

import { convertUserToGenericPatient } from '@/utils/patientUtils.ts';

interface PatientSearchHook {
    data: SearchResult | undefined;
    selected_patient: GenericPatient | undefined;
    is_loading: boolean;
    is_validating: boolean;
    selected_item: string;
    handleSelect: (value: string) => void;
    onChangeSearch: (value: string) => void;
    clean: () => void;
    search_string: string;
}

const MINIMUM_SEARCH_LENGTH = 3;

const usePatientSearch = (
    onPatientSelect?: (patient: GenericPatient | undefined) => void,
    selected_instance_id?: string
): PatientSearchHook => {
    const [selected_item, setSelectedItem] = useState<string>('');
    const [selected_patient, setSelectedPatient] = useState<GenericPatient | undefined>(undefined);
    const [search, setSearch] = useState<string>('');
    const { instance_ids, getInstanceConfig } = useConfig();

    const instance_ids_to_search = selected_instance_id ? [selected_instance_id] : instance_ids;

    const {
        data,
        isLoading: is_loading,
        isValidating: is_validating,
    } = useSWR(
        search
            ? {
                  key: `patient-search-${search.toLowerCase().trim()}`,
                  instance_ids: instance_ids_to_search,
              }
            : undefined,
        () => searchUserOnAllInstances(instance_ids_to_search, search)
    );

    const handleSelect = (value: string): void => {
        setSelectedItem(selected_item === value ? '' : value);

        if (selected_item === value) {
            // deselect
            setSelectedPatient(undefined);
            onPatientSelect?.(undefined);
        } else {
            // select
            const users = data?.patients.map((patient) => patient.user) ?? [];
            const selected_user = users.find((user) => {
                const config = getInstanceConfig(user.instance_id);
                if (!config) return false;

                if (config.pvs_provider === PVSProvider.DOC_CIRRUS) {
                    return (user as NormalizedPatient).user_id === value;
                } else if (config.pvs_provider === PVSProvider.DATA_CUR) {
                    return (user as EnrichedDataCurUser).id === value;
                }
                return false;
            });

            if (!selected_user) {
                return;
            }

            const config = getInstanceConfig(selected_user?.instance_id);
            if (!config) {
                return;
            }

            const patient = convertUserToGenericPatient(selected_user, config.pvs_provider);

            setSelectedPatient(patient);
            onPatientSelect?.(patient);
        }
    };

    const handleSearch = debounce((search_value: string) => {
        setSearch(search_value);
    }, 400);

    const onChangeSearch = (value: string): void => {
        if (value.length < MINIMUM_SEARCH_LENGTH) return;

        handleSearch(value);
    };

    const clean = (): void => {
        setSearch('');
        setSelectedPatient(undefined);
        setSelectedItem('');
    };

    return {
        data: data,
        selected_patient: selected_patient,
        is_loading: is_loading,
        is_validating: is_validating,
        selected_item: selected_item,
        handleSelect: handleSelect,
        onChangeSearch: onChangeSearch,
        clean: clean,
        search_string: search,
    };
};

export default usePatientSearch;
